import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

const ProductsPage = () => {
  const products = [
    {
      id: 'calcite-powder',
      name: 'Calcite Powder',
      category: 'Calcium Carbonate',
      description: 'High-purity natural calcium carbonate derived from limestone, available in various mesh sizes and whiteness grades.',
      keyApplications: ['Paint & Coatings', 'Plastic & PVC', 'Rubber', 'Paper', 'Construction'],
      grades: '200 mesh, 300 mesh, 400 mesh, 600 mesh, 800 mesh'
    },
    {
      id: 'bentonite',
      name: 'Bentonite',
      category: 'Aluminium Phyllosilicate',
      description: 'Premium quality sodium and calcium bentonite clay with excellent swelling and binding properties.',
      keyApplications: ['Drilling Fluids', 'Foundry', 'Civil Engineering', 'Pet Litter', 'Agriculture'],
      grades: 'Sodium Bentonite, Calcium Bentonite, Activated Bentonite'
    },
    {
      id: 'pcc',
      name: 'Precipitated Calcium Carbonate (PCC)',
      category: 'Synthetic Calcium Carbonate',
      description: 'Ultra-fine precipitated calcium carbonate with controlled particle size and morphology for premium applications.',
      keyApplications: ['Paper', 'Paint', 'Plastics', 'Pharmaceuticals', 'Food'],
      grades: 'Scalenohedral, Rhombohedral, Aragonite forms'
    },
    {
      id: 'tcp',
      name: 'Tricalcium Phosphate (TCP)',
      category: 'Calcium Phosphate',
      description: 'Food and pharmaceutical grade TCP meeting IP/BP/USP standards for nutritional and therapeutic applications.',
      keyApplications: ['Nutritional Supplements', 'Animal Feed', 'Pharmaceuticals', 'Food Additives'],
      grades: 'Food Grade, Feed Grade, Pharmaceutical Grade'
    },
    {
      id: 'dcp',
      name: 'Dicalcium Phosphate (DCP)',
      category: 'Calcium Phosphate',
      description: 'High bioavailability DCP for animal nutrition and pharmaceutical formulations with precise phosphorus content.',
      keyApplications: ['Poultry Feed', 'Cattle Feed', 'Aqua Feed', 'Pharmaceutical Excipient'],
      grades: 'Feed Grade (18% P), Pharmaceutical Grade'
    },
    {
      id: 'starch',
      name: 'Starch',
      category: 'Carbohydrate Polymer',
      description: 'Industrial-grade modified and native starch from maize, tapioca, and potato sources.',
      keyApplications: ['Paper', 'Textiles', 'Adhesives', 'Food', 'Corrugation'],
      grades: 'Native Starch, Modified Starch, Cationic Starch'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Our Products</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Comprehensive range of industrial chemicals and minerals sourced from verified manufacturers
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product) => (
              <Card key={product.id} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-600">
                <CardContent className="p-8">
                  <div className="mb-4">
                    <span className="text-sm font-semibold text-blue-600 uppercase">{product.category}</span>
                    <h2 className="text-3xl font-bold text-slate-900 mt-2">{product.name}</h2>
                  </div>
                  
                  <p className="text-gray-700 mb-6 leading-relaxed">{product.description}</p>
                  
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-2">Key Applications:</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.keyApplications.map((app, idx) => (
                        <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-6 p-4 bg-gray-100 rounded-lg">
                    <h3 className="font-bold text-slate-900 mb-2">Available Grades:</h3>
                    <p className="text-gray-700 text-sm">{product.grades}</p>
                  </div>
                  
                  <Link to={`/products/${product.id}`}>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                      View Detailed Specifications <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging & Supply Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Packaging & Supply Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <Card className="border-2">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Standard Packaging</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 25 kg HDPE bags</li>
                    <li>• 50 kg PP bags</li>
                    <li>• 500 kg jumbo bags</li>
                    <li>• 1000 kg bulk bags (FIBC)</li>
                    <li>• Loose bulk (for large orders)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Bulk Supply Capability</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Minimum Order: 1 MT</li>
                    <li>• Typical Lead Time: 7-15 days</li>
                    <li>• Pan-India delivery network</li>
                    <li>• Custom packaging available</li>
                    <li>• Technical support included</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Need Technical Specifications?</h3>
              <p className="text-gray-600 mb-6">Download detailed datasheets or request a customized quote</p>
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8">
                  REQUEST QUOTE & DATASHEETS
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
